//@flow
// Centralized error reporting to the errorlog REST API.
//
// This module exposes two entry points:
// 1. reportError(payload)         — manual / programmatic reporting
// 2. initGlobalErrorHandlers()    — installs window.onerror and
//                                   window.onunhandledrejection listeners
//
// Reports are deduplicated within a rolling window (default 60s) keyed on
// the error message + first line of the stack, so a tight render loop or a
// repeatedly-failing poller does not flood the backend.
//
// All network calls are fire-and-forget — failures are logged to console
// but never re-thrown, so the reporter can never break the caller.

const API_HOST = process.env.NODE_ENV === 'development'
    ? ''
    : 'https://gigsdata.com'

const REPORT_URL = `${API_HOST}/rest/api/v2/errorlog/report`
const SOURCE = 'admin-frontend'
const DEDUPE_WINDOW_MS = 60 * 1000

// In-memory dedupe cache: signature -> last-sent timestamp
const recentReports: Map<string, number> = new Map()

// Cap the map so a long-lived tab with many unique errors doesn't grow
// unbounded. Least-recently-inserted entries are evicted first.
const MAX_RECENT = 200

const signatureOf = (message: string, stack: ?string): string => {
    // Error.prototype.stack starts with the message itself on line 0; the
    // first actual call frame is on line 1. Use line 1 so distinct errors
    // with the same message text but different call sites get distinct
    // signatures and aren't collapsed by dedupe.
    let firstFrame = ''
    if (stack) {
        const lines = String(stack).split('\n')
        firstFrame = lines.length > 1 ? lines[1] : lines[0]
    }
    return `${message}|${firstFrame}`
}

const shouldSkip = (sig: string): boolean => {
    const now = Date.now()
    const last = recentReports.get(sig)
    if (last != null && now - last < DEDUPE_WINDOW_MS) {
        return true
    }
    // Evict oldest if we've hit the cap
    if (recentReports.size >= MAX_RECENT) {
        const oldestKey = recentReports.keys().next().value
        if (oldestKey != null) recentReports.delete(oldestKey)
    }
    recentReports.set(sig, now)
    return false
}

const readUserId = (): string => {
    try {
        const raw = localStorage.getItem('user')
        if (!raw) return ''
        const parsed = JSON.parse(raw)
        const info = parsed && parsed.user && parsed.user.userInfo
        if (!info) return ''
        return info.username || info.email || info.id || ''
    } catch (e) {
        return ''
    }
}

type ReportInput = {
    severity?: string,
    errorType?: string,
    errorMessage: string,
    functionName?: string,
    stackTrace?: string,
    metadata?: any,
}

export const reportError = (input: ReportInput): void => {
    if (!input || !input.errorMessage) return

    const sig = signatureOf(input.errorMessage, input.stackTrace)
    if (shouldSkip(sig)) return

    const payload = {
        severity: input.severity || 'error',
        errorType: input.errorType || 'JavaScriptError',
        errorMessage: String(input.errorMessage).slice(0, 2000),
        functionName: input.functionName || `${SOURCE}/unknown`,
        stackTrace: input.stackTrace ? String(input.stackTrace).slice(0, 8000) : '',
        source: SOURCE,
        metadata: {
            pageUrl: (typeof window !== 'undefined' && window.location) ? window.location.href : '',
            userAgent: (typeof navigator !== 'undefined') ? navigator.userAgent : '',
            userId: readUserId(),
            ...(input.metadata || {}),
        },
    }

    // Fire and forget. Never throw.
    try {
        fetch(REPORT_URL, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            // keepalive lets the request complete even if the tab is closing
            keepalive: true,
        }).catch((e) => {
            // Swallow network errors — do not recurse into reportError
            if (typeof console !== 'undefined' && console.warn) {
                console.warn('[errorReporter] failed to send report:', e)
            }
        })
    } catch (e) {
        if (typeof console !== 'undefined' && console.warn) {
            console.warn('[errorReporter] failed to send report:', e)
        }
    }
}

// Manual bug report from the Report Bug dialog. Resolves true on success
// so the UI can show feedback. Touches the dedupe cache so an identical
// auto-captured error within the window won't re-fire right after.
export const submitBugReport = async (input: ReportInput): Promise<boolean> => {
    if (!input || !input.errorMessage) return false

    // Touch dedupe so identical auto-captures within the window don't re-fire
    const sig = signatureOf(input.errorMessage, input.stackTrace)
    recentReports.set(sig, Date.now())

    const payload = {
        severity: input.severity || 'error',
        errorType: input.errorType || 'UserReport',
        errorMessage: String(input.errorMessage).slice(0, 2000),
        functionName: input.functionName || `${SOURCE}/user-report`,
        stackTrace: input.stackTrace ? String(input.stackTrace).slice(0, 8000) : '',
        source: SOURCE,
        metadata: {
            pageUrl: (typeof window !== 'undefined' && window.location) ? window.location.href : '',
            userAgent: (typeof navigator !== 'undefined') ? navigator.userAgent : '',
            userId: readUserId(),
            reportType: 'manual',
            ...(input.metadata || {}),
        },
    }

    try {
        const response = await fetch(REPORT_URL, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        })
        return response.ok
    } catch (e) {
        if (typeof console !== 'undefined' && console.warn) {
            console.warn('[errorReporter] failed to submit bug report:', e)
        }
        return false
    }
}

let globalHandlersInstalled = false

export const initGlobalErrorHandlers = (): void => {
    if (globalHandlersInstalled) return
    if (typeof window === 'undefined') return
    globalHandlersInstalled = true

    window.addEventListener('error', (event: any) => {
        try {
            const msg = (event && event.message) || 'Unknown error'
            const stack = event && event.error && event.error.stack
                ? event.error.stack
                : ''
            reportError({
                severity: 'error',
                errorType: 'JavaScriptError',
                errorMessage: msg,
                stackTrace: stack,
                functionName: `${SOURCE}/window.onerror`,
                metadata: {
                    filename: event && event.filename,
                    lineno: event && event.lineno,
                    colno: event && event.colno,
                },
            })
        } catch (e) {
            // never throw from the handler
        }
    })

    window.addEventListener('unhandledrejection', (event: any) => {
        try {
            const reason = event && event.reason
            let msg = 'Unhandled promise rejection'
            let stack = ''
            if (reason instanceof Error) {
                msg = reason.message || msg
                stack = reason.stack || ''
            } else if (typeof reason === 'string') {
                msg = reason
            } else if (reason && typeof reason === 'object') {
                msg = reason.message || JSON.stringify(reason)
                stack = reason.stack || ''
            }
            reportError({
                severity: 'error',
                errorType: 'UnhandledPromiseRejection',
                errorMessage: msg,
                stackTrace: stack,
                functionName: `${SOURCE}/unhandledrejection`,
            })
        } catch (e) {
            // never throw from the handler
        }
    })
}

// Helper for API response interceptors — report 5xx and network failures.
export const reportApiFailure = (
    url: string,
    method: string,
    status: number,
    responseText: ?string,
): void => {
    reportError({
        severity: status >= 500 ? 'error' : 'warning',
        errorType: 'ApiError',
        errorMessage: `${method} ${url} failed with status ${status}`,
        functionName: `${SOURCE}/api-call`,
        metadata: {
            url,
            method,
            status,
            responsePreview: responseText ? String(responseText).slice(0, 500) : '',
        },
    })
}
